// The workspace's HTTP surface (slice 05, issue 745): the person's own
// document under a base-system label, and their account-wide settings. Every
// call is `/api/me/...` — there is no address for anyone else's layout.
import {
  DEFAULT_SETTINGS, textLook, type DeviceClass, type LayoutDoc, type UserSettings, type ViewId, emptyLayoutDoc, parseLayoutDoc, type SettingsPatch,
} from '@dragons-table/vtt-shared';
import { requestJson } from './api';

// The device class (issue 1179) is named only when it is not the desktop, where every layout before it lives.
const at = (label: string, device: DeviceClass = 'desktop') =>
  `/api/me/layout?base_system=${encodeURIComponent(label)}${device === 'desktop' ? '' : `&device=${device}`}`;

/** The stored document, or the empty one when none is stored yet or the call failed. */
export async function fetchLayout(label: string, device: DeviceClass = 'desktop', fetchImpl: typeof fetch = fetch): Promise<LayoutDoc> {
  const { status, body } = await requestJson(at(label, device), { method: 'GET' }, fetchImpl);
  if (status !== 200) return emptyLayoutDoc();
  const doc = (body as { doc?: unknown } | null)?.doc;
  return (doc ? parseLayoutDoc(doc) : null) ?? emptyLayoutDoc();
}

/** Store the document; `views` names the windows whose current arrangement this write speaks for (the server keeps the rest). */
export async function saveLayout(label: string, doc: LayoutDoc, views: readonly ViewId[], device: DeviceClass = 'desktop', fetchImpl: typeof fetch = fetch): Promise<boolean> {
  const { status } = await requestJson(`${at(label, device)}&views=${views.join(',')}`, { method: 'PUT', body: JSON.stringify(doc) }, fetchImpl);
  return status === 200;
}

export async function deleteLayout(label: string, device: DeviceClass = 'desktop', fetchImpl: typeof fetch = fetch): Promise<boolean> {
  const { status } = await requestJson(at(label, device), { method: 'DELETE' }, fetchImpl);
  return status === 204;
}

/**
 * Carry a layout to this screen (issue 1182): the person's whole document under this label, from one class of device to
 * the other ("use my laptop layout on this tablet"). The copied document, or the server's words when there is nothing to copy.
 */
export async function copyLayout(label: string, from: DeviceClass, to: DeviceClass, fetchImpl: typeof fetch = fetch): Promise<{ doc: LayoutDoc } | { reason: string }> {
  const { status, body } = await requestJson(`/api/me/layout/copy?base_system=${encodeURIComponent(label)}`, { method: 'POST', body: JSON.stringify({ from, to }) }, fetchImpl);
  const doc = status === 200 ? parseLayoutDoc((body as { doc?: unknown } | null)?.doc) : null;
  if (doc) return { doc };
  const reason = (body as { reason?: unknown } | null)?.reason;
  return { reason: typeof reason === 'string' ? reason : 'The layout could not be copied just now.' };
}

function settingsFrom(body: unknown): UserSettings | null {
  if (typeof body !== 'object' || body === null) return null;
  const b = body as Record<string, unknown>;
  if (typeof b.onboardingSeen !== 'boolean' || typeof b.offerPresets !== 'boolean') return null;
  // The site's theme and the palette (issue 1041): present only when not the default.
  return { onboardingSeen: b.onboardingSeen, offerPresets: b.offerPresets, confirmDelete: b.confirmDelete !== false,
    ...(b.theme === 'light' ? { theme: 'light' as const } : {}), ...(b.palette === 'okabe-ito' ? { palette: 'okabe-ito' as const } : {}),
    // The text size and typeface (issue 1174): present only when not the default.
    ...textLook(b.textSize, b.typeface),
    // The side outlines (qa-runs#1157): present only when off.
    ...(b.outlines === false ? { outlines: false as const } : {}),
    // The status icons on tokens (qa-runs#1440): present only when switched to text.
    ...(b.statusIcons === false ? { statusIcons: false as const } : {}) };
}

/** The settings, or null when they could not be read (the caller decides what a failed read means). */
export async function fetchSettings(fetchImpl: typeof fetch = fetch): Promise<UserSettings | null> {
  const { status, body } = await requestJson('/api/me/settings', { method: 'GET' }, fetchImpl);
  return status === 200 ? settingsFrom(body) : null;
}

export async function saveSettings(patch: SettingsPatch, fetchImpl: typeof fetch = fetch): Promise<UserSettings> {
  const { status, body } = await requestJson('/api/me/settings', { method: 'PUT', body: JSON.stringify(patch) }, fetchImpl);
  const { palette, textSize, typeface, outlines, statusIcons, ...rest } = patch;
  return (status === 200 && settingsFrom(body)) || { ...DEFAULT_SETTINGS, ...rest, ...(palette === 'okabe-ito' ? { palette } : {}), ...textLook(textSize, typeface), ...(outlines === false ? { outlines: false as const } : {}), ...(statusIcons === false ? { statusIcons: false as const } : {}) };
}
